import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesId, TimeFormat } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Company } from '~hooks/useCompany/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';

import { RARELY_CHANGED_DATA_STALE_TIME_IN_MS } from './constants';

const COMPANY_QUERY_KEY = 'company';

interface UseCompanyQueryProps {
  companyId?: string;
}

export default function useCompanyQuery({ companyId }: UseCompanyQueryProps) {
  const { userStore } = useStores();
  const isOwnCompany = Boolean(companyId) && companyId === userStore.userCompany.id;

  const { data, isLoading } = useQuery({
    queryKey: [COMPANY_QUERY_KEY, companyId],
    queryFn:
      companyId && !isOwnCompany
        ? async ({ signal }) => {
            try {
              const response = await getV1CompaniesId({
                path: { id: companyId },
                signal,
              });

              return Company.parse(response.data.data);
            } catch (error) {
              if (signal.aborted) {
                throw error;
              }

              connection.handleRequestError(
                error,
                t('error_messages.company.failed_to_fetch_company'),
              );
            }
          }
        : skipToken,
    // handleRequestError already toasts and reports, so React Query's default of three
    // retries would surface one failure four times.
    retry: false,
    staleTime: RARELY_CHANGED_DATA_STALE_TIME_IN_MS,
  });

  const company = isOwnCompany ? userStore.userCompany : data;

  return {
    company,
    isLoading,
    timeFormat: company?.timeFormat ?? TimeFormat['12_HOUR'],
  };
}
